import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { computed, action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { alias } from '@ember/object/computed';
import { isPresent } from '@ember/utils';
import { dropTask } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';

import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import MessageLimitModel from 'cio/models/message-limit';
import MessageSettingsService from 'cio/services/message-settings';
import {
  isAutoRetryWindowCorrect,
  MAX_MESSAGE_RETRY_WINDOW_SECS,
  DEFAULT_MESSAGE_RETRY_WINDOW_SECS,
} from 'cio/utils/message-auto-retry';
import { SECONDS_IN_DAY } from 'cio/utils/constants';

type EditableAttr = 'count' | 'windowSecs' | 'retryWindowSecs';

export interface MessageLimitProxy {
  count: string | number;
  windowSecs: number;
  retryWindowSecs: number;
}

const EDITABLE_ATTRS: EditableAttr[] = ['windowSecs', 'retryWindowSecs', 'count'];

const Validations = {
  count(value: string) {
    if (isPresent(value) && parseInt(value) <= 0) {
      return 'Must be a positive number';
    }

    return true;
  },
};

export default class ActionsMessagingLimitIndex extends Controller {
  @service messageSettings!: MessageSettingsService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;

  messageLimitProxy!: MessageLimitProxy;
  validations!: FormValidations;
  readonly maxTimeframe = 60 * 60 * 24 * 7; // 7 days

  @tracked isSettingUp = false;
  @tracked showConfirmDisableMessageLimitModal = false;
  @tracked showConfirmDisableAutoRetryModal = false;
  @tracked windowSecsIsValid = true;
  @tracked retryWindowSecsIsValid = true;

  readonly maxMessageRetryWindowSecs = MAX_MESSAGE_RETRY_WINDOW_SECS;

  @alias('messageSettings.messageLimit') messageLimit!: MessageLimitModel;
  @alias('messageLimit.count') messageLimitCount!: number;
  @alias('messageSettings.isMessagingLimitEnabled') isMessagingLimitEnabled!: boolean;

  @computed(
    'isMessagingLimitEnabled',
    'isSettingUp',
    'messageLimitProxy.{windowSecs,retryWindowSecs,count}',
    'messageLimit.{windowSecs,retryWindowSecs,count}',
  )
  get isDirty() {
    if (!this.isMessagingLimitEnabled && !this.isSettingUp) {
      return false;
    } else {
      return EDITABLE_ATTRS.some((attr) => {
        return parseInt(this.messageLimitProxy[attr] as string) !== this.messageLimit[attr];
      });
    }
  }

  @computed('messageLimitProxy.retryWindowSecs')
  get isAutoRetryEnabled() {
    return this.messageLimitProxy.retryWindowSecs > 0;
  }

  setDefaults() {
    this.setProperties({
      isSettingUp: false,
      showConfirmDisableMessageLimitModal: false,
      windowSecsIsValid: true,
      retryWindowSecsIsValid: true,
    });
  }

  setProxy() {
    let count = 1;
    let windowSecs = SECONDS_IN_DAY;
    let retryWindowSecs = DEFAULT_MESSAGE_RETRY_WINDOW_SECS;

    if (this.isMessagingLimitEnabled) {
      count = this.messageLimit.count;
      windowSecs = this.messageLimit.windowSecs;
      retryWindowSecs = this.messageLimit.retryWindowSecs;
    }

    this.set('messageLimitProxy', { count, windowSecs, retryWindowSecs });
  }

  _updateProxyAttr(key: EditableAttr, value: number) {
    this.set('messageLimitProxy', {
      ...this.messageLimitProxy,
      [key]: value,
    });

    this.analytics.track('workspace', 'message_limits_edit_started', {
      editType: 'limit_change',
      model: this.messageLimitProxy,
    });
  }

  saveSettings = dropTask(async ({ count, windowSecs, retryWindowSecs }: MessageLimitProxy) => {
    try {
      this.messageLimit.setProperties({ count: parseInt(count as string), windowSecs, retryWindowSecs });

      await this.messageLimit.save();

      this.set('isSettingUp', false);
      this.alerts.success('message_limit_update.success', 'Your settings have been saved.');
    } catch (e) {
      this.apiErrors.report(e);
      this.messageLimit.rollbackAttributes();
    } finally {
      this.setProxy();
    }
  });

  @action
  resetSettings() {
    this.messageLimit.rollbackAttributes();
    this.setProxy();

    if (this.messageLimit.count === 0) {
      this.set('isSettingUp', false);
    }
  }

  @action
  startSetup() {
    this.set('isSettingUp', true);
    this.analytics.track('workspace', 'message_limits_edit_started', {
      editType: 'enable',
      model: this.messageLimit,
    });
  }

  @action
  setLimitsCount(count: number) {
    this._updateProxyAttr('count', count);
    this.validations.validateAttribute('count');
  }

  @action
  setLimitsTimeWindow(windowSecs: number) {
    this._updateProxyAttr('windowSecs', windowSecs);
  }

  @action
  setLimitsTimeRetryWindow(retryWindowSecs: number) {
    if (isAutoRetryWindowCorrect(retryWindowSecs)) {
      this._updateProxyAttr('retryWindowSecs', retryWindowSecs);
    }
  }

  @action
  disableLimit() {
    this.set('showConfirmDisableMessageLimitModal', true);
    this.analytics.track('workspace', 'message_limits_edit_started', {
      editType: 'disable',
      model: this.messageLimit,
    });
  }

  @action
  switchAutoRetry() {
    let switchIsOn = this.messageLimitProxy.retryWindowSecs > 0;
    if (switchIsOn) {
      if (this.messageLimit.retryWindowSecs > 0) {
        this.showConfirmDisableAutoRetryModal = true;
      } else {
        this.disableAutoRetry();
      }
    } else {
      this.enableAutoRetry();
    }
  }

  @action
  enableAutoRetry() {
    this._updateProxyAttr('retryWindowSecs', DEFAULT_MESSAGE_RETRY_WINDOW_SECS);
    this.retryWindowSecsIsValid = true;
  }

  @action
  disableAutoRetry() {
    this._updateProxyAttr('retryWindowSecs', 0);
    this.retryWindowSecsIsValid = true;
    this.showConfirmDisableAutoRetryModal = false;
  }

  @action
  confirmDisableLimit() {
    this.saveSettings.perform({
      count: 0,
      windowSecs: 0,
      retryWindowSecs: 0,
    });

    this.setDefaults();
  }

  constructor() {
    super(...arguments);

    this.set(
      'validations',
      FormValidations.create({
        validations: Validations,
        context: this,
        modelKey: 'messageLimitProxy',
      }),
    );
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'actions/messaging-limit/index': ActionsMessagingLimitIndex;
  }
}
